import { useRevisionScanIdQueryParam } from '../../../components/projects/hooks/useRevisionScanIdQueryParam';
import { useParams } from '../../../contexts/RouterContext';
import { useAddToast } from '../../../contexts/ToastContext';
import { queryClient } from '../../../ReactQuery';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import { ISSUE_CATEGORY_SUMMARY_QUERY_KEY_PREFIX } from '../issues/useIssueCategorySummary';
import type { RevisionIssueScanResponse } from '../../../../shared/types/routes/revisions';
import type { RevisionRouteParams } from '../../../components/projects/projectDashboardTypes';

import { getQueryKey as getRevisionQueryKey } from './useRevision';
import { getRevisionScansQueryKeyPrefix } from './useRevisionScans';

//  Function to kick off an issue scan for a given revision.
function scanRevision(revisionLocator: string, params?: { steady?: boolean }) {
  return get<RevisionIssueScanResponse>(`/api/revisions/${nuc(revisionLocator)}/issues`, false, params);
}

/**
 * Mutation which will kick off an issue scan for a given revision.
 * Automatically invalidates the revision query on success to refetch updated data
 * and points the `revisionScanId` URL param at the new scan so the scan dropdown
 * reflects it. Shows success/error toasts on completion.
 */
export default function useScanRevision() {
  const addToast = useAddToast();
  const { setRevisionScanId } = useRevisionScanIdQueryParam();
  const params = useParams<RevisionRouteParams>();

  return useToastedMutation({
    mutationFn: scanRevision,
    errorToastMessage: 'Failed to scan for issues',
    onSuccess: async (response, revisionLocator) => {
      // Bail if the user navigated away from the revision being scanned before the response came back.
      // The route's revision locator is reconstructed from URL path params.
      const currentLocator = params ? `${params.project_id}$${params.revision_id}` : '';
      if (currentLocator !== revisionLocator) {
        return;
      }
      // Invalidate revision, scans, and category summary queries so both refetch after scan completes.
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getRevisionQueryKey(revisionLocator) }),
          queryClient.invalidateQueries({ queryKey: getRevisionScansQueryKeyPrefix(revisionLocator) }),
          queryClient.invalidateQueries({ queryKey: ISSUE_CATEGORY_SUMMARY_QUERY_KEY_PREFIX }),
        ]);
        // Point the URL at the new scan so the dropdown's selection follows the fresh scan.
        const newScanId = response?.data?.revisionScan?.id;
        if (newScanId) {
          setRevisionScanId(newScanId);
        }
        addToast({
          type: 'success',
          content: 'Issue scan completed successfully',
        });
      } catch {
        addToast({
          type: 'error',
          content: 'Issue scan completed, but refreshing the page data failed. Please reload to see the latest results.',
        });
      }
    },
  });
}
